import { memo, useState, type CSSProperties, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { getCountries } from "libphonenumber-js";
import {
  AuraLayout,
  Button,
  GradientText,
  Layout,
  SupportIcon,
} from "lola-framework-ui-test";
import { BodyCopy } from "../../components/BodyCopy";
import { FlagCircle } from "../../components/FlagCircle";
import { Popup } from "../../components/Popup";
import { SmartShell } from "../../components/smart/SmartShell";
import { Button as SmartButton } from "../../components/smart/ui/Button";
import { useSupportSheet } from "../../components/SupportSheet";
import { TITLE_COLOR } from "../../constants";
import { useStore } from "../../store/globalState";
import { countryDisplayName } from "../../utils/country-display";
import {
  dialCodeForCountry,
  exampleNationalDigits,
  formatNationalInput,
  isNationalPhoneValid,
  phoneInputPlaceholder,
} from "../../utils/phonePlaceholder";
import { Navbar } from "../../components/Navbar";

export type PhoneCaptureState = "empty" | "invalid" | "saving" | "error" | "other";

export type PhoneChrome = "need-help" | "plain" | "smart";

const PHONE_COUNTRIES = getCountries();

const initialCountryCode = (state: PhoneCaptureState): string =>
  state === "other" ? "MX" : "US";

const initialDigits = (state: PhoneCaptureState, countryCode: string): string => {
  if (state === "invalid") return "202";
  if (state === "saving" || state === "error") return "3055551234";
  if (state === "other") return exampleNationalDigits(countryCode);
  return "";
};

const usePhoneCapture = (state: PhoneCaptureState) => {
  const { t, i18n } = useTranslation("phoneCapture");
  const frozen = state === "saving";
  const [countryCode, setCountryCodeState] = useState(() => initialCountryCode(state));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [value, setValue] = useState(() =>
    formatNationalInput(initialCountryCode(state), initialDigits(state, initialCountryCode(state))),
  );
  const [touched, setTouched] = useState(state === "invalid");
  const iso = PHONE_COUNTRIES.includes(countryCode as (typeof PHONE_COUNTRIES)[number])
    ? countryCode
    : "US";
  const dial = dialCodeForCountry(iso);
  const placeholder = phoneInputPlaceholder(iso);
  const countryName = countryDisplayName(iso, i18n.language || "en") || iso;
  const digits = value.replace(/\D/g, "");
  const valid = isNationalPhoneValid(iso, value);
  const showInvalid = state === "invalid" || (touched && digits.length > 0 && !valid);
  const showSaveError = state === "error";
  const canContinue = !frozen && state !== "invalid" && valid;
  const setCountryCode = (next: string) => {
    setCountryCodeState(next);
    setValue((current) => formatNationalInput(next, current));
  };
  const onInput = (raw: string, cursor: number | null) => {
    const prevDigits = value.replace(/\D/g, "");
    let nextDigits = raw.replace(/\D/g, "");
    if (nextDigits === prevDigits && raw.length < value.length) {
      const digitsBefore = raw.slice(0, cursor ?? raw.length).replace(/\D/g, "").length;
      const dropAt = Math.max(0, digitsBefore - 1);
      nextDigits = prevDigits.slice(0, dropAt) + prevDigits.slice(dropAt + 1);
    }
    setValue(formatNationalInput(iso, nextDigits));
  };
  return {
    t,
    i18n,
    frozen,
    iso,
    countryName,
    dial,
    placeholder,
    digits,
    value,
    onInput,
    onBlur: () => setTouched(true),
    setCountryCode,
    pickerOpen,
    setPickerOpen,
    showInvalid,
    showSaveError,
    canContinue,
    openPicker: () => {
      if (!frozen) setPickerOpen(true);
    },
  };
};

type PhoneCapture = ReturnType<typeof usePhoneCapture>;

interface PhoneFieldColors {
  rest: string;
  active: string;
  error: string;
  hint: string;
}

/** Mirrors `FloatingField`: a gradient slot cannot paint a 1px ring, so fall back to a solid. */
const solidColor = (color: string | undefined, fallback: string) =>
  !color || color.includes("gradient(") ? fallback : color;

const PhoneCaptureCard = ({
  capture,
  accent,
  titleColor,
  bodyColor,
  field,
}: {
  capture: PhoneCapture;
  accent: string;
  titleColor: string;
  bodyColor: string;
  field: PhoneFieldColors;
}) => {
  const {
    t,
    frozen,
    iso,
    countryName,
    dial,
    placeholder,
    digits,
    value,
    onInput,
    onBlur,
    showInvalid,
    showSaveError,
    openPicker,
  } = capture;
  return (
    <section
      className="phone-capture-card"
      style={
        {
          "--phone-accent": accent,
          "--phone-title": titleColor,
          "--phone-body": bodyColor,
          "--phone-field-rest": field.rest,
          "--phone-field-active": field.active,
          "--phone-field-error": field.error,
          "--phone-field-hint": field.hint,
        } as CSSProperties
      }
    >
      <p className="phone-capture-heading">{t("heading")}</p>
      <label className="phone-capture-label" htmlFor="phone-capture-input">
        {t("label")}
      </label>
      <div
        className={[
          "phone-capture-field",
          digits.length > 0 ? "is-filled" : "",
          showInvalid || showSaveError ? "is-invalid" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <button
          type="button"
          className="phone-capture-country"
          disabled={frozen}
          aria-label={t("country.change", { country: countryName })}
          onClick={openPicker}
        >
          <FlagCircle iso2={iso.toLowerCase()} size={28} />
          <span>{dial}</span>
        </button>
        <input
          id="phone-capture-input"
          className="phone-capture-input"
          inputMode="tel"
          autoComplete="tel"
          placeholder={placeholder}
          aria-invalid={showInvalid || showSaveError}
          readOnly={frozen}
          value={value}
          onChange={(event) => onInput(event.target.value, event.target.selectionStart)}
          onBlur={onBlur}
        />
      </div>
      {showInvalid ? <p className="phone-capture-error">{t("invalid")}</p> : null}
      {showSaveError ? <p className="phone-capture-error">{t("saveError")}</p> : null}
      <BodyCopy className="phone-capture-note" textColor={bodyColor}>
        {t("description")}
      </BodyCopy>
    </section>
  );
};

const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const PhoneCountryPicker = ({
  capture,
  titleColor,
  ink,
  ring,
  field,
  chrome = "plain",
}: {
  capture: PhoneCapture;
  titleColor: string;
  ink: string;
  ring: string;
  field: PhoneFieldColors;
  /** Smart uses theme tokens + bottom sheet; lola chrome keeps shared Popup. */
  chrome?: PhoneChrome;
}) => {
  const { t, i18n } = capture;
  const [query, setQuery] = useState("");
  const locale = i18n.language || "en";
  const options = PHONE_COUNTRIES.map((code) => ({
    code,
    name: countryDisplayName(code, locale) || code,
    dial: dialCodeForCountry(code),
  })).sort((a, b) => a.name.localeCompare(b.name, locale));
  const needle = normalize(query.trim());
  const filtered = needle
    ? options.filter((option) =>
        [option.name, option.code, option.dial].some((value) =>
          normalize(value).includes(needle),
        ),
      )
    : options;
  const close = () => {
    setQuery("");
    capture.setPickerOpen(false);
  };
  const isSmart = chrome === "smart";
  const pickerStyle = {
    "--phone-ink": ink,
    "--phone-field-rest": field.rest,
    "--phone-field-active": field.active,
    "--phone-field-hint": field.hint,
    "--phone-option-ring": ring,
    ...(isSmart
      ? {
          "--phone-option-border": "var(--color-border)",
          "--phone-flag-bg": "var(--color-bg-muted)",
        }
      : null),
  } as CSSProperties;

  const pickerBody = (
    <section className="phone-capture-picker" style={pickerStyle}>
      {isSmart ? (
        <h2 className="phone-capture-picker-title text-fg">{t("country.select")}</h2>
      ) : (
        <GradientText
          as="h2"
          textColor={titleColor}
          className="phone-capture-picker-title h2"
        >
          {t("country.select")}
        </GradientText>
      )}
      <input
        type="text"
        className="phone-capture-picker-search"
        value={query}
        placeholder={t("country.search")}
        aria-label={t("country.search")}
        autoComplete="off"
        onChange={(event) => setQuery(event.target.value)}
      />
      <ul className="phone-capture-picker-list">
        {filtered.length === 0 ? (
          <li className="phone-capture-picker-empty">{t("country.empty")}</li>
        ) : null}
        {filtered.map((option) => {
          const selected = option.code === capture.iso;
          return (
            <li key={option.code}>
              <button
                type="button"
                className={`phone-capture-picker-option${selected ? " is-selected" : ""}`}
                aria-pressed={selected}
                onClick={() => {
                  capture.setCountryCode(option.code);
                  close();
                }}
              >
                <span className="phone-capture-picker-flag">
                  <FlagCircle iso2={option.code.toLowerCase()} size={24} />
                </span>
                <span className="phone-capture-picker-name">{option.name}</span>
                <span className="phone-capture-picker-dial">{option.dial}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );

  if (isSmart) {
    if (!capture.pickerOpen) return null;
    return (
      <div
        className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40"
        onClick={close}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t("country.select")}
          className="bg-bg text-fg max-h-[85dvh] w-full max-w-md overflow-hidden rounded-t-2xl px-6 pt-5 pb-8"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="mb-1 flex justify-end">
            <button
              type="button"
              className="text-fg-muted text-sm font-semibold"
              onClick={close}
            >
              {t("country.cancel")}
            </button>
          </div>
          {pickerBody}
        </div>
      </div>
    );
  }

  return (
    <Popup visible={capture.pickerOpen} onClose={close}>
      {pickerBody}
    </Popup>
  );
};

const PhoneActions = ({ capture }: { capture: PhoneCapture }) => {
  const { theme } = useStore();
  const { background, backgroundBtn, textColorBtn } = theme.whiteView;
  const { t, frozen, canContinue } = capture;
  return (
    <section className="footer-section phone-capture-actions">
      <Button
        type="button"
        size="large"
        variant="outline"
        background={backgroundBtn}
        color={TITLE_COLOR}
        style={{ backgroundColor: background }}
        disabled={frozen}
        onClick={() => undefined}
      >
        {t("skip")}
      </Button>
      <Button
        size="large"
        background={backgroundBtn}
        color={textColorBtn}
        disabled={!canContinue}
        onClick={() => undefined}
      >
        {frozen ? t("saving") : t("continue")}
      </Button>
    </section>
  );
};

interface PhoneNumberPageProps {
  state: PhoneCaptureState;
  needHelp?: boolean;
  namespace?: string;
}

export const PhoneNumberPage = memo(
  ({ state, needHelp = false, namespace = "send" }: PhoneNumberPageProps) => {
    const capture = usePhoneCapture(state);
    const { t: tFlow } = useTranslation(namespace);
    const { theme } = useStore();
    const { title, bodyCopy, dropzoneColors, subtitile, errorColor, backgroundBtn } =
      theme.whiteView;
    const { openSupport, supportSheet } = useSupportSheet(namespace);
    const ink = solidColor(bodyCopy, "#979797");
    const field: PhoneFieldColors = {
      rest: solidColor(subtitile, "#979797"),
      active: ink,
      error: solidColor(errorColor, "#dc251c"),
      hint: "var(--muted-foreground, #61616b)",
    };

    return (
      <>
        <AuraLayout
          colorConfig={theme.whiteView}
          className={`phone-capture-page${needHelp ? " need-help-header" : ""}`}
        >
          <Layout.Header>
            {needHelp ? (
              <Navbar
                title={tFlow("common.needHelp")}
                align="right"
                noBackButton
                onIconClick={openSupport}
                icon={<SupportIcon size={35} colors={dropzoneColors} />}
              />
            ) : (
              <Navbar title={capture.t("title")} align="center" color={title} noBackButton />
            )}
          </Layout.Header>
          <Layout.Content>
            <PhoneCaptureCard
              capture={capture}
              accent={dropzoneColors[0]}
              titleColor={bodyCopy}
              bodyColor={bodyCopy}
              field={field}
            />
          </Layout.Content>
          <Layout.Footer>
            <PhoneActions capture={capture} />
          </Layout.Footer>
        </AuraLayout>
        {needHelp ? supportSheet : null}
        <PhoneCountryPicker
          capture={capture}
          titleColor={bodyCopy}
          ink={ink}
          ring={backgroundBtn}
          field={field}
        />
      </>
    );
  },
);

const SmartPhoneNumberPage = memo(({ state }: { state: PhoneCaptureState }) => {
  const capture = usePhoneCapture(state);
  const { t, frozen, canContinue } = capture;
  const field: PhoneFieldColors = {
    rest: "var(--color-border)",
    active: "var(--color-fg)",
    error: "var(--color-danger)",
    hint: "var(--color-fg-muted)",
  };
  return (
    <SmartShell title={t("title")}>
      <div className="phone-capture-page">
        <PhoneCaptureCard
          capture={capture}
          accent="var(--color-accent)"
          titleColor="var(--color-fg)"
          bodyColor="var(--color-fg-muted)"
          field={field}
        />
        <div className="mt-4 flex flex-col gap-2">
          <SmartButton variant="ghost" fullWidth disabled={frozen} onClick={() => undefined}>
            {t("skip")}
          </SmartButton>
          <SmartButton fullWidth size="lg" disabled={!canContinue} onClick={() => undefined}>
            {frozen ? t("saving") : t("continue")}
          </SmartButton>
        </div>
      </div>
      <PhoneCountryPicker
        capture={capture}
        titleColor="var(--color-fg)"
        ink="var(--color-fg)"
        ring="var(--color-accent)"
        field={field}
        chrome="smart"
      />
    </SmartShell>
  );
});

/** Stable gallery page. Built once per flow and state. */
export const phoneScreen = (
  state: PhoneCaptureState,
  chrome: PhoneChrome,
  namespace?: string,
): ComponentType => {
  const Page = memo(() =>
    chrome === "smart" ? (
      <SmartPhoneNumberPage state={state} />
    ) : (
      <PhoneNumberPage
        state={state}
        needHelp={chrome === "need-help"}
        namespace={namespace}
      />
    ),
  );
  Page.displayName = `Phone-${chrome}-${state}`;
  return Page;
};
